import { useState } from 'react';
import { useAuth } from 'contexts/ProtectedRoute';
import { AppDocument } from 'api';
import { DocStatus, getTranslatedStatuses, DocStatusTranslations } from 'data/constants/DocStatus';
import Popover from '@mui/material/Popover';
import ROLE from '../../../../../../../../assets/userRoles.json';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import IconBtn from 'components/common/IconBtn';
import { ArrowDownIcon } from 'views/Dashboards-B2B/components/Table.styled';
import { TextForActionsDropdown } from './ViewEditDocumentPopoverMenu.styled';
import { ReactComponent as ChangeStatus } from 'assets/icons/B2B/cached.svg';
import * as S from './SelectDocStatusDropdown.styled';

type SelectDocStatusDropdownProps = {
    document: AppDocument;
    handleChangeStatus: (document: AppDocument, newDocStatus: DocStatus) => void;
    mobile: boolean;
    actionsPopover: boolean;
    handleCloseActionsPopover?: () => void;
};

const SelectDocStatusDropdown = ({
    document,
    handleChangeStatus,
    mobile,
    actionsPopover,
    handleCloseActionsPopover,
}: SelectDocStatusDropdownProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { role } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const canSetApprovedStatus =
        role !== ROLE.backofficeAdmin && role !== ROLE.backofficeConsultant;

    const copyDocStatus = { ...DocStatus };
    const { APPROVED, ...enumWithoutApproved } = copyDocStatus;

    const listOfStatuses = canSetApprovedStatus ? DocStatus : enumWithoutApproved;

    const translatedStatuses = getTranslatedStatuses(language, listOfStatuses);

    const translatedToEnglishMapping = Object.keys(listOfStatuses).reduce(
        (acc, key) => {
            acc[DocStatusTranslations[language as 'EN' | 'FR'][key]] = key;
            return acc;
        },
        {} as { [key: string]: string }
    );

    const formatStatus = (status: string) => {
        const tempStatus = status.toLowerCase().split('_').join(' ');
        return tempStatus.charAt(0).toUpperCase() + tempStatus.slice(1);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleOptionClick = (e: React.MouseEvent<HTMLElement>, selectedStatus: string) => {
        e.stopPropagation();
        const englishStatus = translatedToEnglishMapping[selectedStatus];
        handleChangeStatus(document, englishStatus as DocStatus);
        handleClose();
        handleCloseActionsPopover?.();
    };

    return (
        <>
            <S.BurgerMenu
                isActive={!!anchorEl}
                onClick={(event) => {
                    handleClick(event);
                }}
            >
                {actionsPopover ? (
                    <>
                        <IconBtn
                            style={{
                                padding: '0px',
                                height: '22px',
                                width: '22px',
                                marginRight: '9px',
                            }}
                        >
                            <ChangeStatus fill="#282828" />
                        </IconBtn>
                        <TextForActionsDropdown>
                            {t('changeStatus', { lng: language })}
                        </TextForActionsDropdown>
                    </>
                ) : (
                    <ArrowDownIcon />
                )}
            </S.BurgerMenu>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                marginThreshold={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'visible',
                            mt: '8px',
                            minWidth: '7rem',
                            padding: '0',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.09)',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <S.DropDownMenu>
                    {Object.values(translatedStatuses).map((option: string, i: number) => {
                        return (
                            <S.MenuItem key={i} onClick={(e) => handleOptionClick(e, option)}>
                                <S.Item>{formatStatus(option)}</S.Item>
                            </S.MenuItem>
                        );
                    })}
                </S.DropDownMenu>
            </Popover>
        </>
    );
};

export default SelectDocStatusDropdown;
